import React, { useEffect, useMemo, useState, lazy, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, AlertTriangle, BookOpen, FileText, HelpCircle, PlayCircle, ListChecks } from 'lucide-react';
import { getContentSections, readContentCache, ContentSection, SectionType } from '../../api/content';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
const TYPE_META: Record<SectionType, {
  label: string;
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  tint: string;
  iconBg: string;
  iconColor: string;
}> = {
  article: {
    label: 'Article',
    icon: FileText,
    tint: 'bg-primary/10 text-primary',
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary'
  },
  faq: {
    label: 'FAQ',
    icon: HelpCircle,
    tint: 'bg-accent/15 text-accent',
    iconBg: 'bg-accent/15',
    iconColor: 'text-accent'
  },
  video: {
    label: 'Video',
    icon: PlayCircle,
    tint: 'bg-amber-500/15 text-amber-600',
    iconBg: 'bg-amber-500/15',
    iconColor: 'text-amber-600'
  },
  guide: {
    label: 'Guide',
    icon: ListChecks,
    tint: 'bg-blue-500/10 text-blue-600',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-600'
  }
};
export function LearnHome() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sections, setSections] = useState<ContentSection[]>(() => readContentCache() || []);
  const [isLoading, setIsLoading] = useState(sections.length === 0);
  const [error, setError] = useState(false);
  const fetchContent = async () => {
    if (sections.length === 0) setIsLoading(true);
    setError(false);
    try {
      const fresh = await getContentSections();
      setSections(fresh);
    } catch {
      if (sections.length === 0) setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Top-level only, sorted by orderIndex (already sorted in api, but be safe)
  const topLevel = useMemo(() => sections.filter(s => !s.parentId).sort((a, b) => a.orderIndex - b.orderIndex), [sections]);
  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return topLevel;
    return topLevel.filter(s => s.title?.toLowerCase().includes(q) || s.content?.toLowerCase().includes(q));
  }, [topLevel, searchQuery]);
  return <main className="w-full max-w-[1200px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-1278" data-mp="2zw24pcrv7pnj">
      {/* Header */}
      <div className="mb-8" data-id="element-1279" data-mp="2v1xwyrarzdle">
        <h1 className="text-[28px] font-semibold text-text-primary mb-2" data-id="element-1280" data-mp="27e6ltr0z16lp">
          Learn
        </h1>
        <p className="text-[16px] text-text-secondary" data-id="element-1281" data-mp="22fmz3oejzqsa">
          Your pregnancy & baby guide
        </p>
      </div>

      {topLevel.length > 0 && <div className="mb-8 max-w-[600px]" data-id="element-1282" data-mp="hvzvxrfe9b0r">
          <InputField placeholder="Search topics..." icon={<Search size={20} data-id="element-1284" data-mp="mpvryoc3knta" />} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1283" data-mp="mnrgnlbvcmiz" />
        </div>}

      {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1285" data-mp="cyhjhr264do0">
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonBlock key={i} height="180px" borderRadius="20px" data-id="element-1286" data-mp="15mwy4che0tyt" />)}
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center max-w-[600px] mx-auto" data-id="element-1287" data-mp="rndzqbedowzp">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-1288" data-mp="wlfkd0rgd4da" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-1289" data-mp="3cdmmrdexjst8">
            Couldn't load content
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-1290" data-mp="3hcprsxuwtz17">
            There was a problem connecting to the server.
          </p>
          <Button onClick={fetchContent} className="mx-auto" data-id="element-1291" data-mp="i00ftjamzfn">
            Try Again
          </Button>
        </div> : topLevel.length === 0 ? <div className="flex flex-col items-center justify-center text-center py-16" data-id="element-1292" data-mp="3mvrvbdry47c7">
          <div className="w-[120px] h-[120px] rounded-full bg-primary/10 flex items-center justify-center mb-6" data-id="element-1293" data-mp="3iuy86mmbuwkd">
            <BookOpen size={48} className="text-primary" data-id="element-1294" data-mp="jg2skh751cho" />
          </div>
          <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1295" data-mp="egghadave6bi">
            Content coming soon
          </h3>
          <p className="text-[15px] text-text-secondary max-w-[280px]" data-id="element-1296" data-mp="aed2beozsjlh">
            Check back later.
          </p>
        </div> : filteredSections.length === 0 ? <div className="text-center py-12" data-id="element-1297" data-mp="2v53u61q9v1yj">
          <p className="text-[16px] text-text-secondary" data-id="element-1298" data-mp="2v659wji8gcml">
            No topics found matching "{searchQuery}"
          </p>
          <button onClick={() => setSearchQuery('')} className="text-primary font-medium mt-4 hover:underline" data-id="element-1299" data-mp="2q4pmiksl1u11">
            Clear search
          </button>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1300" data-mp="1akoyx3wq56lh">
          {filteredSections.map(sec => {
        const meta = TYPE_META[sec.sectionType] || TYPE_META.article;
        const Icon = meta.icon;
        const childCount = sections.filter(s => s.parentId === sec.id).length;
        return <button key={sec.slug} onClick={() => navigate(`/app/learn/${sec.slug}`)} className="bg-white rounded-[20px] overflow-hidden shadow-card border border-transparent hover:border-primary/20 hover:shadow-elevated transition-all text-left flex flex-col h-full group" data-id="element-1301" data-mp="1tbzmbb38r18m">
                {sec.mediaUrl ? <div className="relative h-[120px] w-full bg-secondary overflow-hidden" data-id="element-1302" data-mp="7zg5ie4nspzv">
                    <img src={sec.mediaUrl} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" loading="lazy" data-id="element-1303" data-mp="1bac1cay2vdxy" />
                    {sec.sectionType === 'video' && <div className="absolute inset-0 flex items-center justify-center bg-black/20" data-id="element-1304" data-mp="1g9f75xlvto6z">
                        <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-lg" data-id="element-1305" data-mp="1u8olssufwa8u">
                          <PlayCircle size={26} className="text-primary" data-id="element-1306" data-mp="1pamuxlqrv6mt" />
                        </div>
                      </div>}
                  </div> : <div className="h-[80px] w-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5" data-id="element-1307" data-mp="1z1hqjgpwrikx">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${meta.iconBg}`} data-id="element-1308" data-mp="281echakwy3ca">
                      <Icon size={22} className={meta.iconColor} data-id="element-1309" data-mp="2rnplpbvsbemq" />
                    </div>
                  </div>}

                <div className="p-5 flex flex-col flex-1" data-id="element-1310" data-mp="2wjwsf6w08u5q">
                  <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-1311" data-mp="2ilq34f54g2ho">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-[11px] font-semibold uppercase tracking-wider ${meta.tint}`} data-id="element-1312" data-mp="2mnbluh4c7azs">
                      {meta.label}
                    </span>
                    {childCount > 0 && <span className="text-[11px] font-medium text-text-secondary" data-id="element-1313" data-mp="1yzaaz1jorih7">
                        {childCount} {childCount === 1 ? 'item' : 'items'}
                      </span>}
                  </div>

                  <h3 className="text-[16px] font-semibold text-text-primary mb-2 leading-tight group-hover:text-primary transition-colors line-clamp-2" data-id="element-1314" data-mp="1pym4crwmgkec">
                    {sec.title}
                  </h3>

                  {(sec.subtitle || sec.description || sec.content) && <p className="text-[13px] text-text-secondary mb-4 flex-1 line-clamp-2" data-id="element-1315" data-mp="25h56x6eem8cc">
                      {sec.subtitle || sec.description || sec.content}
                    </p>}

                  <div className="flex justify-end mt-auto" data-id="element-1316" data-mp="1wemc99krv216">
                    <div className="w-[28px] h-[28px] rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors" data-id="element-1317" data-mp="1rgcdkozu9jat">
                      <ChevronRight size={16} className="text-text-secondary group-hover:text-primary transition-colors" data-id="element-1318" data-mp="1mnj9828a8cz9" />
                    </div>
                  </div>
                </div>
              </button>;
      })}
        </div>}
    </main>;
}